import { api } from "@/lib/api";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { Button, Text } from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, Handshake } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AuditLogTab } from "./detail/AuditLogTab";
import { CommissionsTab } from "./detail/CommissionsTab";
import { CouponDiscountTab } from "./detail/CouponDiscountTab";
import { LinksTab } from "./detail/LinksTab";
import { OverviewTab } from "./detail/OverviewTab";
import { PayoutHistoryTab } from "./detail/PayoutHistoryTab";
import { PayoutOnboardingTab } from "./detail/PayoutOnboardingTab";
import { ReferralsTab } from "./detail/ReferralsTab";
import { AffiliateStatusBadge, LoadingPanel } from "./shared";

type Tab =
  | "overview"
  | "links"
  | "coupon"
  | "referrals"
  | "commissions"
  | "payouts"
  | "onboarding"
  | "audit";

const TABS: { value: Tab; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "links", label: "Links" },
  { value: "coupon", label: "Coupon & Discount" },
  { value: "referrals", label: "Referrals" },
  { value: "commissions", label: "Commissions" },
  { value: "payouts", label: "Payout History" },
  { value: "onboarding", label: "Payout Onboarding" },
  { value: "audit", label: "Audit Log" },
];

export function AffiliateDetailPage() {
  const { affiliateId } = useParams<{ affiliateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as Tab) || "overview";
  const setActiveTab = (tab: Tab) => setSearchParams({ tab });
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["affiliate-detail", affiliateId],
    queryFn: () => api.affiliates.get(affiliateId!),
    enabled: !!affiliateId,
  });

  const invalidateDetail = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["affiliate-detail", affiliateId] }),
    [queryClient, affiliateId],
  );

  const approveMutation = useMutation({
    mutationFn: () => api.affiliates.approve(affiliateId!),
    onSuccess: () => {
      invalidateDetail();
      queryClient.invalidateQueries({ queryKey: ["affiliates"] });
    },
  });

  const gdprMutation = useMutation({
    mutationFn: () => api.affiliates.gdprAnonymize(affiliateId!),
    onSuccess: () => {
      invalidateDetail();
      queryClient.invalidateQueries({ queryKey: ["affiliates"] });
    },
  });

  const handleApprove = async () => {
    const ok = await confirm({
      title: "Approve this affiliate?",
      description:
        "Approving promotes this pending affiliate to active and emails them their tracking links.",
      confirmLabel: "Approve",
      confirmColor: "green",
    });
    if (ok) approveMutation.mutate();
  };

  const handleGdpr = async () => {
    const ok = await confirm({
      title: "GDPR-anonymize this affiliate?",
      description:
        "Scrubs name, email, and channel, invalidates all auth tokens, and disables the account. Commission and payout rows are retained for financial audit. This cannot be undone.",
      confirmLabel: "Anonymize",
      confirmColor: "red",
    });
    if (ok) gdprMutation.mutate();
  };

  const handleCopyId = () => {
    if (!affiliateId) return;
    navigator.clipboard.writeText(affiliateId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) return <LoadingPanel />;

  if (error || !data) {
    return (
      <div className="px-4 py-2">
        <Button plain onClick={() => navigate("/admin/affiliates")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Affiliates
        </Button>
        <div className="mt-8 text-center">
          <Handshake className="mx-auto h-12 w-12 text-gray-400" />
          <Text className="mt-2 text-gray-500 dark:text-zinc-400">Affiliate not found</Text>
        </div>
      </div>
    );
  }

  const { affiliate } = data;

  return (
    <div className="px-4 py-2">
      {/* Header */}
      <div className="mb-8">
        <Button plain onClick={() => navigate("/admin/affiliates")} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Affiliates
        </Button>
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
            <Handshake className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <Text className="text-3xl font-bold text-zinc-950 dark:text-white">
              {affiliate.name || affiliate.email}
            </Text>
            <div className="flex items-center gap-1.5">
              <Text className="text-sm font-mono text-zinc-400 dark:text-zinc-500">
                {affiliate.id}
              </Text>
              <button
                onClick={handleCopyId}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                title="Copy affiliate ID"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <AffiliateStatusBadge status={affiliate.status} />
            {affiliate.status === "pending" && (
              <Button
                color="green"
                onClick={handleApprove}
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? "Approving…" : "Approve"}
              </Button>
            )}
            <Button
              outline
              onClick={handleGdpr}
              disabled={gdprMutation.isPending}
              className="text-red-600 dark:text-red-400"
            >
              {gdprMutation.isPending ? "Anonymizing..." : "GDPR anonymize"}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-0 border-b border-zinc-200 dark:border-zinc-700">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.value
                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && <OverviewTab detail={data} />}
      {activeTab === "links" && (
        <LinksTab affiliateId={affiliate.id} links={data.links} onChange={invalidateDetail} />
      )}
      {activeTab === "coupon" && (
        <CouponDiscountTab affiliate={affiliate} coupon={data.coupon} onChange={invalidateDetail} />
      )}
      {activeTab === "referrals" && <ReferralsTab referrals={data.referrals} />}
      {activeTab === "commissions" && <CommissionsTab commissions={data.commissions} />}
      {activeTab === "payouts" && <PayoutHistoryTab payouts={data.payouts} />}
      {activeTab === "onboarding" && (
        <PayoutOnboardingTab onboarding={affiliate.payoutOnboarding} />
      )}
      {activeTab === "audit" && <AuditLogTab affiliateId={affiliate.id} />}
    </div>
  );
}
